import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Nearshore } from '../../../core/models/nearshore';
import { NearshoreService } from '../../../core/services/nearshor-service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NearshoreFormComponent } from './nearshore-form/nearshore-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';

@Component({
  selector: 'em-nearshore',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective,
  ],
  templateUrl: './nearshore.component.html',
  styleUrl: './nearshore.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class NearshoreComponent {
  nearshores!: Nearshore[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  constructor(
    private nearshoreService: NearshoreService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getAllNearshores();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.nearShore.addNearShore.title',
        'layout.configurations.nearShore.addNearShore.successMessage.title',
        'layout.configurations.nearShore.addNearShore.successMessage.detail',
        'layout.configurations.nearShore.editNearShore.title',
        'layout.configurations.nearShore.editNearShore.successMessage.title',
        'layout.configurations.nearShore.editNearShore.successMessage.detail',
        'layout.configurations.nearShore.deleteNearShore.title',
        'layout.configurations.nearShore.deleteNearShore.successMessage.title',
        'layout.configurations.nearShore.deleteNearShore.successMessage.detail',
        'layout.configurations.nearShore.deleteNearShore.message',
        'layout.configurations.nearShore.deleteNearShore.yes',
        'layout.configurations.nearShore.deleteNearShore.no',
        'layout.configurations.nearShore.deleteNearShore.errorMessage.title',
        'layout.configurations.nearShore.deleteNearShore.errorMessage.detail',
        'layout.configurations.nearShore.deleteNearShore.infoMessage.title',
        'layout.configurations.nearShore.deleteNearShore.infoMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllNearshores() {
    this.nearshoreService.getNearShores().subscribe((data) => (this.nearshores = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(NearshoreFormComponent, {
      header: this.translations['layout.configurations.nearShore.addNearShore.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((nearshore) => {
      if (nearshore) {
        this.getAllNearshores();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.nearShore.addNearShore.successMessage.title'],
          detail: this.translations['layout.configurations.nearShore.addNearShore.successMessage.detail'],
        });
      }
    });
  }

  onEdit(nearshore: Nearshore) {
    this.ref = this.dialogService.open(NearshoreFormComponent, {
      header: this.translations['layout.configurations.nearShore.editNearShore.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: nearshore,
    });

    this.ref.onClose.subscribe((result) => {
    if (!result) return;

    if (result.type === 'updated') {
        this.getAllNearshores();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.nearShore.editNearShore.successMessage.title'],
          detail: this.translations['layout.configurations.nearShore.editNearShore.successMessage.detail'],
        });
      }
       else if (result.type === 'no-change') {
        this.messageService.add({
        severity: 'warn',
        summary:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
        detail:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage')
      });
       }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.nearShore.deleteNearShore.message'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.nearShore.deleteNearShore.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.nearShore.deleteNearShore.yes'],
      rejectLabel: this.translations['layout.configurations.nearShore.deleteNearShore.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteNearshore(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.nearShore.deleteNearShore.infoMessage.title'],
          detail: this.translations['layout.configurations.nearShore.deleteNearShore.infoMessage.detail'],
          life: 3000,
        });
      },
    });
  }

  deleteNearshore(id: number) {
    this.nearshoreService.deleteNearShore(id).subscribe({
      next: () => {
        this.getAllNearshores();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.nearShore.deleteNearShore.successMessage.title'],
          detail: this.translations['layout.configurations.nearShore.deleteNearShore.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting nearshore: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.nearShore.deleteNearShore.errorMessage.title'],
          detail: this.translations['layout.configurations.nearShore.deleteNearShore.errorMessage.detail'],
        });
      },
    });
  }
}
